import { IComparablesTransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { ZooplaComparableDTO, ZooplaComparableLocation } from "react-migration/lib/typings/Zoopla";
import { EnrichedComparableDTO } from "react-migration/lib/typings/EnrichedComparables";

/**
 * Correlates LandTech data with Zoopla data based on UPRN
 * @param landTechData Array of LandTech comparable transactions
 * @param zooplaData Array of Zoopla comparable data
 * @returns Array of enriched comparable data with optional Zoopla enhancement
 */
function correlateByUPRN(
  landTechData: IComparablesTransactionDTO[],
  zooplaData: ZooplaComparableDTO[]
): EnrichedComparableDTO[] {
  const zooplaByUprn = new Map(
    zooplaData.filter((z) => z != null && z.uprn != null && z.uprn !== "").map((z) => [z.uprn, z])
  );

  // Create a map for LandTech records with UPRNs
  const ltByUprn = new Map(
    landTechData.filter((lt) => lt != null && lt.uprn != null && lt.uprn !== "").map((lt) => [lt.uprn, lt])
  );

  // Create a collection for LandTech records without UPRNs
  const ltWithoutUprn = landTechData.filter((lt) => lt != null && (lt.uprn === null || lt.uprn === ""));

  const combinedUprns = new Set([...zooplaByUprn.keys(), ...ltByUprn.keys()]);

  // Start with records that have UPRNs
  let enriched = Array.from(combinedUprns).map((uprn) => {
    if (!uprn) {
      return null;
    }
    const ltData = ltByUprn.get(uprn);
    const zooplaData = zooplaByUprn.get(uprn);

    const defaultLandTechData = {
      address: "",
      category: null,
      country: null,
      currency: "GBP",
      date_of_transfer: "",
      distance_from_center: null,
      est_bedrooms: null,
      estimated_bedrooms_max: null,
      estimated_bedrooms_min: null,
      num_bedrooms: null,
      est_price: null,
      est_price_per_square_metre: null,
      geometry: null,
      is_new_property: null,
      owner_type: "unknown",
      number_habitable_rooms: null,
      post_code: null,
      prev_transactions: null,
      price: null,
      price_per_square_metre: null,
      region: null,
      sub_category: null,
      tenure: null,
      total_floor_area: null,
      town: null,
      transaction_id: "unknown",
      designation: null,
    };

    const getAddressDetail = (detail: string | null | undefined, last = false, uppercase = false) => {
      if (!detail) {
        return "";
      }
      return `${uppercase ? detail.trim().toUpperCase() : detail.trim().toLowerCase()}${last ? "" : ", "}`;
    };

    const getAddress = (address: ZooplaComparableLocation["address"] | null | undefined) => {
      if (!address) {
        return null;
      }
      const addressParts = Object.entries(address).filter(
        ([key, value]) => value !== null && key !== "__typename" && key !== "countryCode"
      );

      const formattedAddress = addressParts
        .map(([key, value], index) => {
          return getAddressDetail(value, index === addressParts.length - 1, key === "postcode");
        })
        .join("");
      return formattedAddress;
    };

    const address = getAddress(zooplaData?.location?.address) ?? ltData?.address;

    const enrichedData = {
      ...(ltData
        ? ltData
        : {
            ...defaultLandTechData,
            uprn: zooplaData?.uprn,
            address,
            post_code: zooplaData?.location?.address?.postcode,
            is_new_property: undefined,
            price: null,
            designation: undefined,
            category: undefined,
            sub_category: undefined,
          }),
      zooplaEnrichment: zooplaData
        ? {
            attributes: zooplaData.attributes,
            valuation: zooplaData.valuation,
            correlationStatus: ltData ? "matched" : "zoopla_only",
          }
        : {
            attributes: {} as ZooplaComparableDTO["attributes"],
            valuation: {} as ZooplaComparableDTO["valuation"],
            correlationStatus: "no_zoopla",
          },
    };

    return enrichedData;
  });

  // Now add records without UPRNs
  const recordsWithoutUprn = ltWithoutUprn.map((ltData) => ({
    ...ltData,
    zooplaEnrichment: {
      attributes: {} as ZooplaComparableDTO["attributes"],
      valuation: {} as ZooplaComparableDTO["valuation"],
      correlationStatus: "no_zoopla",
    },
  }));

  // Combine both sets of records
  enriched = [...enriched, ...recordsWithoutUprn];

  return enriched.filter((e) => e != null) as EnrichedComparableDTO[];
}

export { correlateByUPRN };
